import { initializeApp } from 'firebase/app'
import { FirebaseApp } from 'firebase/app'
import {
  getAnalytics,
  logEvent,
  setUserId,
  setUserProperties,
} from 'firebase/analytics'

/**
 * Firebase configuration for Zwizle
 * Replace these placeholders with your actual Firebase project details
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

// Check if Firebase config is valid (at least API key should be present)
const isFirebaseConfigValid =
  !!firebaseConfig.apiKey &&
  firebaseConfig.apiKey !== 'your-api-key' &&
  firebaseConfig.apiKey !== 'undefined'

// Initialize Firebase app and analytics only if config is valid
let app: FirebaseApp | null = null
let analytics = null

// Only initialize if we have a valid config
if (isFirebaseConfigValid && typeof window !== 'undefined') {
  try {
    app = initializeApp(firebaseConfig)
    analytics = getAnalytics(app)
    console.log('Firebase analytics initialized successfully')
  } catch (error) {
    console.error('Firebase initialization failed:', error)
  }
} else {
  console.warn('Firebase analytics disabled: Missing configuration')
}

export { app }

/**
 * Track a custom event in Firebase Analytics
 *
 * @param eventName - The name of the event to log
 * @param eventParams - Additional parameters to log with the event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, string | number | boolean>
) => {
  if (!analytics) {
    return
  }

  try {
    logEvent(analytics, eventName, eventParams)
  } catch (error) {
    console.error(`Error logging event ${eventName}:`, error)
  }
}

/**
 * Set a unique identifier for the current user
 * Only use this if you have user authentication
 *
 * @param uid - Anonymous user identifier
 */
export const setAnalyticsUserId = (uid: string) => {
  if (!analytics) return

  try {
    setUserId(analytics, uid)
  } catch (error) {
    console.error('Error setting user ID:', error)
  }
}

/**
 * Set user properties for analytics segmentation
 *
 * @param properties - User properties to set
 */
export const setAnalyticsUserProperties = (
  properties: Record<string, string>
) => {
  if (!analytics) return

  try {
    setUserProperties(analytics, properties)
  } catch (error) {
    console.error('Error setting user properties:', error)
  }
}

export default { trackEvent, setAnalyticsUserId, setAnalyticsUserProperties }
